import React, { useState } from 'react';
import { CornerDownRight } from 'lucide-react';
import MentionInput, { renderWithMentions } from './MentionInput';

const buildTree = (comments) => {
  const byId = new Map();
  const roots = [];
  for (const c of comments) byId.set(c.id, { ...c, replies: [] });
  for (const c of comments) {
    const node = byId.get(c.id);
    if (c.parent_comment_id && byId.has(c.parent_comment_id)) {
      byId.get(c.parent_comment_id).replies.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
};

const CommentNode = ({ node, depth = 0, brandUsers, onReply }) => {
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');

  const submitReply = async () => {
    if (!replyText.trim()) return;
    await onReply(replyText, node.id);
    setReplyText('');
    setReplying(false);
  };

  return (
    <div className={depth > 0 ? 'ml-5 pl-3 border-l border-gray-200' : ''}>
      <div className="bg-gray-50 p-2.5 rounded-lg">
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {renderWithMentions(node.comment, brandUsers)}
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[11px] text-gray-500">
            {node.user_name || 'Unknown'} · {new Date(node.created_at).toLocaleString()}
          </span>
          <button
            onClick={() => setReplying((r) => !r)}
            className="text-[11px] text-gray-500 hover:text-blue-600 flex items-center gap-1"
          >
            <CornerDownRight size={11} />
            Reply
          </button>
        </div>
      </div>

      {replying && (
        <div className="mt-2 flex gap-2 ml-5">
          <MentionInput
            as="textarea"
            value={replyText}
            onChange={setReplyText}
            brandUsers={brandUsers}
            rows={2}
            autoFocus
            placeholder="Write a reply… (@ to mention)"
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submitReply();
              } else if (e.key === 'Escape') {
                setReplying(false);
                setReplyText('');
              }
            }}
          />
          <button
            onClick={submitReply}
            disabled={!replyText.trim()}
            className="px-3 py-1 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed self-start"
          >
            Reply
          </button>
        </div>
      )}

      {node.replies.length > 0 && (
        <div className="mt-2 space-y-2">
          {node.replies.map((child) => (
            <CommentNode key={child.id} node={child} depth={depth + 1} brandUsers={brandUsers} onReply={onReply} />
          ))}
        </div>
      )}
    </div>
  );
};

const CommentThread = ({ comments = [], brandUsers, onReply }) => {
  const tree = buildTree(comments);
  if (tree.length === 0) return <p className="text-sm text-gray-500">No comments yet</p>;
  return (
    <div className="space-y-3">
      {tree.map((root) => (
        <CommentNode key={root.id} node={root} brandUsers={brandUsers} onReply={onReply} />
      ))}
    </div>
  );
};

export default CommentThread;
